import { createContext, useCallback, useContext, useState } from 'react';

const STORAGE_KEY = 'profile.rail.collapsed';

/**
 * Whether the profile rail is minimized, and the way back out of it.
 *
 * A context rather than props threaded through the sidebar, for the reason
 * `ProfileSectionTitleContext` is one: the `sidebar` slot takes an opaque `ReactNode`, so the
 * blocks inside it — `<ProfileFacts>`, `<ProfileNav>` — have no prop path from the shell that
 * owns the state. A block added later reads the rail's state by existing.
 *
 * `expand` is here because the collapsed rail cannot draw everything: a nav GROUP has nowhere
 * to put its children at 64px, so picking one opens the rail and lets the group open normally.
 * A control that cannot complete its own job hands the reader back the room to do it, rather
 * than growing a second navigation surface beside the first.
 */
export const ProfileRailContext = createContext<{ collapsed: boolean; expand: () => void }>({
  collapsed: false,
  expand: () => {},
});

export function useProfileRail() {
  return useContext(ProfileRailContext);
}

/**
 * Collapsed/expanded state for the profile rail, remembered across sessions.
 *
 * Defaults to EXPANDED and stores only an explicit collapse — the same contract
 * `useSidebarCollapsed` has for the app rail, deliberately written to the same shape so the two
 * rails cannot drift in how they remember. The key is its own: minimizing a 201 file's panel
 * picker says nothing about wanting the app's navigation gone too.
 */
export function useProfileRailCollapsed() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      // Blocked storage is a preference we cannot remember, never a reason to fail.
      return false;
    }
  });

  const set = useCallback((next: boolean) => {
    setCollapsed(next);

    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // Remembering the state is a convenience, never a requirement.
    }
  }, []);

  const toggle = useCallback(() => set(!collapsed), [collapsed, set]);
  const expand = useCallback(() => set(false), [set]);

  return { collapsed, toggle, expand };
}
